// Unified message-slot model for the conversation stack.
//
// A timeline position is a **slot** holding N **variants**, one active. "The
// original" is simply `variants[0]`. A regenerated response ("reroll
// alternative") is just another variant in the same slot — the product treats
// them as peers, so the code now models them as one thing.
//
// Canonical state (the conversations store) is a flat `MessageSlot[]`. Everything
// that only wants the flat, canonical timeline (AI context building, director
// notes, sorting, "is most recent AI") reads the derived `messages` projection —
// each slot flattened to its active variant. Only the variant-aware surfaces
// (ProseMessage / MessageList) consume slots directly.
//
// Lifecycle is stored once on the variant as `phase`; projections are phase-only.

import type { Message, DirectorNoteValue } from '@/types/domain'

export type VariantOrigin = 'message' | 'alternative'
export type MessagePhase = 'thinking' | 'streaming' | 'complete' | 'error'

/**
 * One generation at a slot position. `variants[0]` (origin `'message'`) is what
 * used to be "the message"; further variants (origin `'alternative'`) are
 * rerolls. `origin` is load-bearing for UI behavior: the database now stores
 * both anchors and reroll variants in `messages`, but the store still needs to
 * distinguish the original from child variants.
 */
export interface Variant {
  id: number | string
  origin: VariantOrigin
  content: string
  phase: MessagePhase
  stream_id?: string | null
  /** Persistence is independent of generation completion. */
  saveRequestId?: string
  saveState?: 'saving' | 'pending'
  // Ephemeral, in-memory only (never persisted): live reasoning while it streams
  // and a snapshot kept after the live copy is cleared. Per-variant, uniformly.
  reasoning_content?: string | null
  reasoning_snapshot?: string | null
}

/** A timeline position holding N variants, one active. */
export interface MessageSlot {
  /** The canonical `messages.id` — stable slot identity across variant switches. */
  id: number | string
  conversation_id?: number | string | null
  is_user_author?: boolean
  /** User slots only — DB-enforced. AI variants never carry a director note. */
  director_note?: DirectorNoteValue | null
  created_at?: string
  updated_at?: string
  t?: string
  client_key?: string
  variants: Variant[]
  activeVariantId: number | string
}

/** Message with client-side transient properties: the flat projection of a slot. */
export type StoreMessage = Omit<Message, 'status' | 'is_streaming' | 'is_complete' | 'director_note'> & {
  phase: MessagePhase
  director_note?: DirectorNoteValue | null
  status?: never
  is_streaming?: never
  is_complete?: never
  t?: string
  reasoning_content?: string | null
  reasoning_snapshot?: string | null
}

/** Loose shape covering both store messages and mapped alternative rows. */
type VariantSource = {
  id: number | string
  content?: string | null
  phase?: MessagePhase
  stream_id?: string | null
  reasoning_content?: string | null
  reasoning_snapshot?: string | null
}

function toVariant(source: VariantSource, origin: VariantOrigin): Variant {
  return {
    id: source.id,
    origin,
    content: source.content ?? '',
    phase: source.phase ?? 'complete',
    stream_id: source.stream_id ?? null,
    reasoning_content: source.reasoning_content ?? null,
    reasoning_snapshot: source.reasoning_snapshot ?? null,
  }
}

export const variantFromMessage = (msg: VariantSource): Variant => toVariant(msg, 'message')
export const variantFromAlternative = (alt: VariantSource): Variant => toVariant(alt, 'alternative')

type SlotSource = VariantSource & {
  conversation_id?: number | string | null
  is_user_author?: boolean
  director_note?: DirectorNoteValue | null
  created_at?: string
  updated_at?: string
  t?: string
  client_key?: string
}

/** Build a single-variant slot from a (store or mapped) message. */
export function slotFromMessage(msg: SlotSource): MessageSlot {
  const variant = variantFromMessage(msg)
  return {
    id: msg.id,
    conversation_id: msg.conversation_id,
    is_user_author: msg.is_user_author,
    director_note: msg.director_note ?? null,
    created_at: msg.created_at,
    updated_at: msg.updated_at,
    t: msg.t,
    client_key: msg.client_key,
    variants: [variant],
    activeVariantId: variant.id,
  }
}

/** The currently-selected variant, falling back to the original. */
export function getActiveVariant(slot: MessageSlot): Variant {
  return (
    slot.variants.find((v) => String(v.id) === String(slot.activeVariantId)) ??
    slot.variants[0]
  )
}

/**
 * Flatten a slot to its active variant's content/lifecycle/reasoning while
 * keeping the slot's canonical `messages.id` as identity. This is the derived
 * `messages` timeline every flat-list reader consumes.
 */
export function projectSlotToMessage(slot: MessageSlot): StoreMessage {
  const variant = getActiveVariant(slot)
  return {
    id: slot.id,
    conversation_id: slot.conversation_id,
    is_user_author: slot.is_user_author,
    director_note: slot.director_note ?? null,
    created_at: slot.created_at,
    updated_at: slot.updated_at,
    t: slot.t,
    client_key: slot.client_key,
    content: variant.content,
    phase: variant.phase,
    stream_id: variant.stream_id ?? undefined,
    reasoning_content: variant.reasoning_content ?? null,
    reasoning_snapshot: variant.reasoning_snapshot ?? null,
  } as StoreMessage
}

export const projectSlotsToMessages = (slots: MessageSlot[]): StoreMessage[] =>
  slots.map(projectSlotToMessage)

/** Non-original variants of a slot (the rerolls), in order. */
export const alternativeVariants = (slot: MessageSlot): Variant[] =>
  slot.variants.filter((v) => v.origin === 'alternative')

/**
 * Anything that carries a timeline position: a slot, a raw fetched message, or
 * an in-flight optimistic message. Ordering only needs identity + timestamps.
 */
export interface TimelineOrderable {
  id?: number | string | null
  t?: string | null
  created_at?: string | null
  updated_at?: string | null
}

/**
 * The numeric value of a persisted `messages.id`, or `null` for anything that
 * isn't a real serial id — notably the `temp-*` ids optimistic/streaming
 * messages carry before the server assigns one. `Number('temp-…')` is `NaN`,
 * so those cleanly report `null` and get treated as "not yet persisted".
 */
function timelineNumericId(id: number | string | null | undefined): number | null {
  if (typeof id === 'number') return Number.isFinite(id) ? id : null
  if (typeof id === 'string' && id.trim() !== '') {
    const n = Number(id)
    return Number.isFinite(n) ? n : null
  }
  return null
}

const timelineTime = (entry: TimelineOrderable): number =>
  new Date(entry.t || entry.created_at || entry.updated_at || 0).getTime()

/**
 * Canonical conversation order is **insertion order**, i.e. the `bigint` serial
 * `messages.id`. This is stable against edited/skewed timestamps and gives a
 * deterministic tiebreak for messages sharing a `t` (a user+assistant pair
 * written in the same tick).
 *
 * Optimistic messages have no real id yet, so any persisted (numeric-id)
 * message sorts before any temp one — temps are always the newest, pinned to
 * the bottom — and two temps fall back to their insertion timestamp, which
 * preserves the streaming append order exactly as the old time-sort did.
 */
export function compareTimelineEntries(a: TimelineOrderable, b: TimelineOrderable): number {
  const aId = timelineNumericId(a.id)
  const bId = timelineNumericId(b.id)
  if (aId !== null && bId !== null) {
    if (aId !== bId) return aId - bId
    return timelineTime(a) - timelineTime(b)
  }
  if (aId !== null) return -1
  if (bId !== null) return 1
  return timelineTime(a) - timelineTime(b)
}
